import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { selectActiveOrg, useAuthStore } from '../auth/store';
import type { PublicReport } from './types';

// Wire types — mirror backend/src/modules/hours-report/hours-report.service.ts#get.

export interface ReportEntry {
  id: string;
  date: string;
  personName: string;
  taskKey: string;
  taskName: string;
  notes: string | null;
  hours: number;
}

export interface DraftGroup {
  id: string;
  taskKey: string;
  label: string;
}

export interface Draft {
  groups: DraftGroup[];
  assign: Record<string, string>;
}

export interface ViewGroup {
  id: string | null;
  label: string;
  hours: number;
  entryCount: number;
  entries: ReportEntry[];
}

export interface ViewTask {
  key: string;
  name: string;
  hours: number;
  percent: number;
  entryCount: number;
  groups: ViewGroup[];
}

export interface HoursReportDetail {
  report: {
    id: string;
    title: string;
    periodFrom: string | null;
    periodTo: string | null;
    showNames: boolean;
    showEntries: boolean;
    groupedBy: 'claude' | 'notes' | null;
    groupedAt: string | null;
    updatedAt: string;
    createdBy: string;
  };
  agreement: { id: string; name: string; client: string };
  draft: Draft;
  view: { totalHours: number; entryCount: number; ungroupedHours: number; tasks: ViewTask[] };
  preview: PublicReport;
  share: {
    live: boolean;
    url: string | null;
    sharedAt: string | null;
    sharedBy: string | null;
    revokedAt: string | null;
    /** The shared copy matches what sharing now would freeze. */
    upToDate: boolean;
  };
  grouping: { available: boolean; reason: string | null; model: string };
  /** Only on a create or regroup with Claude: what the grouping could not place. */
  notice?: string | null;
}

export interface HoursReportSummary {
  id: string;
  title: string;
  periodFrom: string | null;
  periodTo: string | null;
  shared: boolean;
  updatedAt: string;
}

const key = (id: string) => ['hours-reports', 'detail', id] as const;

export function useAgreementReports(agreementId: string | undefined) {
  return useQuery({
    queryKey: ['hours-reports', 'list', agreementId],
    queryFn: () =>
      api.get<{
        canRun: boolean;
        grouping: { available: boolean; reason: string | null };
        reports: HoursReportSummary[];
      }>('/hours-reports', { params: { agreementId } }),
    enabled: !!agreementId,
  });
}

export function useHoursReport(id: string | undefined) {
  return useQuery({
    queryKey: key(id ?? ''),
    queryFn: () => api.get<HoursReportDetail>(`/hours-reports/${id}`),
    enabled: !!id,
  });
}

/** Every write answers with the whole report; it replaces the cache in place. */
function useReportWrite<I>(run: (input: I) => Promise<HoursReportDetail>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (detail) => {
      qc.setQueryData(key(detail.report.id), detail);
      void qc.invalidateQueries({ queryKey: ['hours-reports', 'list'] });
    },
  });
}

export function useCreateReport() {
  return useReportWrite((input: { agreementId: string; title?: string; from?: string; to?: string; ai?: boolean }) =>
    api.post<HoursReportDetail>('/hours-reports', input),
  );
}

export function useUpdateReport(id: string) {
  return useReportWrite(
    (input: { title?: string; from?: string | null; to?: string | null; showNames?: boolean; showEntries?: boolean }) =>
      api.patch<HoursReportDetail>(`/hours-reports/${id}`, input),
  );
}

export function useSaveDraft(id: string) {
  return useReportWrite((input: { draft: Draft; expectedUpdatedAt: string }) =>
    api.put<HoursReportDetail>(`/hours-reports/${id}/draft`, input),
  );
}

export function useRegroup(id: string) {
  return useReportWrite((input: { mode: 'claude' | 'notes' }) =>
    api.post<HoursReportDetail>(`/hours-reports/${id}/regroup`, input),
  );
}

export function useShareReport(id: string) {
  return useReportWrite(() => api.post<HoursReportDetail>(`/hours-reports/${id}/share`, {}));
}

export function useRevokeReport(id: string) {
  return useReportWrite(() => api.post<HoursReportDetail>(`/hours-reports/${id}/revoke`, {}));
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/hours-reports/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['hours-reports'] }),
  });
}

/**
 * The staff preview PDF — what sharing now would show. A file, so `fetch`
 * rather than the JSON client, with the same Authorization + X-Organization
 * headers (the invoice PDF's pattern), and the server's filename kept.
 */
export async function downloadPreviewPdf(id: string): Promise<void> {
  const state = useAuthStore.getState();
  const activeOrg = selectActiveOrg(state);
  const headers: Record<string, string> = { Accept: 'application/pdf' };
  if (state.accessToken) headers.Authorization = `Bearer ${state.accessToken}`;
  if (activeOrg) headers['X-Organization'] = activeOrg.id;
  const res = await fetch(`/api/hours-reports/${id}/pdf`, { headers });
  if (!res.ok) {
    let message = `Could not build the PDF (${res.status})`;
    try {
      message = ((await res.json()) as { message?: string }).message ?? message;
    } catch {
      /* keep the status message */
    }
    throw new Error(message);
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const fileName = encoded ? decodeURIComponent(encoded[1]) : 'hours-report.pdf';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
